import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Botao, FaixaAmbiente, Marca, Navegacao } from "@plantus/ui";

import { usarAmbiente } from "../ambiente/ProvedorDeAmbiente";
import { usarSessao } from "../autenticacao/ProvedorDeSessao";
import { SETORES } from "../navegacao/setores";

/**
 * A largura em que a navegação deixa de ser gaveta e vira coluna.
 *
 * <p>É o MESMO ponto de quebra do `app.css` (`min-width: 56.01rem`), e a
 * repetição é deliberada: a folha de estilo decide a aparência, e esta consulta
 * decide o que existe no DOM. Se as duas divergirem, aparece a pior combinação
 * possível — a faixa de ícones desenhada dentro de uma gaveta de celular.
 */
const CONSULTA_DE_COLUNA = "(min-width: 56.01rem)";

/**
 * Onde fica a preferência de recolher a lateral — e por que ela é <b>uma
 * chave que só existe quando não é o padrão</b>.
 *
 * <p>É o mesmo princípio dos setores recolhidos (ver `CHAVE_RECOLHIDOS` em
 * `packages/ui/src/Navegacao.tsx`): guarda-se o estado que ESCONDE, nunca o
 * que mostra. Sem a chave, a lateral nasce esticada — com os nomes, os selos
 * de "em construção" e a versão à vista. Valor estragado, versão antiga, mão
 * humana no console: tudo isso não é a palavra guardada, e tudo isso cai no
 * mesmo lado seguro.
 *
 * <p>O outro efeito é o que a auditoria cobra: <b>quem nunca tocar no botão
 * não deixa nada guardado</b>. O armazenamento do cliente continua vazio
 * depois de entrar no sistema, que é uma afirmação verificável — e há teste.
 */
const CHAVE_LATERAL = "plantus.navegacao.lateral-recolhida";
const RECOLHIDA = "faixa";

function lerLateralRecolhida(): boolean {
  try {
    return window.localStorage.getItem(CHAVE_LATERAL) === RECOLHIDA;
  } catch {
    // `localStorage` LANÇA com o armazenamento desligado — navegação privada,
    // política do aparelho. Preferência de interface não vale uma tela em
    // branco.
    return false;
  }
}

function gravarLateralRecolhida(recolhida: boolean): void {
  try {
    if (recolhida) window.localStorage.setItem(CHAVE_LATERAL, RECOLHIDA);
    // Esticada é o padrão, e padrão não se guarda: a chave SAI. Preferência
    // que sobrevive ao próprio desfazer é lixo que vira comportamento
    // inexplicável seis meses depois.
    else window.localStorage.removeItem(CHAVE_LATERAL);
  } catch {
    // Sem armazenamento, a escolha vale só nesta sessão. É o suficiente.
  }
}

/**
 * A janela está larga o bastante para a navegação ser coluna?
 *
 * <p>Pergunta-se ao navegador, com a mesma consulta do CSS — e continua-se
 * ouvindo: quem arrasta a borda da janela de 1400 para 700px precisa receber a
 * gaveta, não uma faixa de ícones espremida. Sem `matchMedia` (jsdom), a
 * resposta é "não", que é a forma sem botão nenhum.
 */
function usarColuna(): boolean {
  const [coluna, definir] = useState(
    () => window.matchMedia?.(CONSULTA_DE_COLUNA).matches === true,
  );

  useEffect(() => {
    const consulta = window.matchMedia?.(CONSULTA_DE_COLUNA);
    if (consulta === undefined) return undefined;

    const aoMudar = () => definir(consulta.matches);
    aoMudar();
    consulta.addEventListener("change", aoMudar);
    return () => consulta.removeEventListener("change", aoMudar);
  }, []);

  return coluna;
}

/**
 * O enquadramento de quem está dentro: faixa, cabeçalho, navegação, conteúdo.
 *
 * <p>No celular a navegação vira <b>gaveta</b>. Não é adaptação de última hora:
 * o "manter conectado" existe porque o sistema é consultado do telefone várias
 * vezes ao dia, e uma coluna fixa de 15rem numa tela de 6 polegadas não deixa
 * espaço para o que se veio ver.
 *
 * <h2>No desktop, a lateral tem duas formas e rolagem própria</h2>
 *
 * <p>Ela é <b>esticada</b> por padrão e pode virar uma <b>faixa de ícones</b>.
 * O botão que troca as duas mora dentro da navegação, e a preferência
 * sobrevive ao recarregamento — ver {@link CHAVE_LATERAL}.
 *
 * <p>Recolher é decisão de quem opera, e <b>só existe onde a navegação é
 * coluna</b>: abaixo de 56rem ela é gaveta que abre e fecha inteira, e um
 * segundo controle de recolhimento ali seria dois botões para a mesma ideia.
 * Por isso `aoAlternarRecolhimento` só é passado no desktop — o botão não
 * existe no DOM do celular, e não existir é diferente de estar escondido.
 *
 * <p>A rolagem própria da lateral é do `app.css`, e a conta de altura é do
 * navegador: ver o bloco "DESKTOP: a lateral rola sozinha".
 */
export function Estrutura({ children }: { children: ReactNode }) {
  const { estado, sair } = usarSessao();
  const { ambiente, versao } = usarAmbiente();
  const navegar = useNavigate();
  const local = useLocation();

  const [gavetaAberta, definirGavetaAberta] = useState(false);
  const [saindo, definirSaindo] = useState(false);

  /*
   * A preferência é lida sempre; quem decide se ela VALE é a largura.
   *
   * Guardar de um jeito e aplicar de outro é de propósito: quem recolheu a
   * lateral na mesa e abre o mesmo sistema no telefone recebe a gaveta
   * inteira, e ao voltar para a mesa reencontra a faixa como a deixou. A
   * preferência não se perde por mudar de aparelho, e não atrapalha onde não
   * se aplica.
   */
  const coluna = usarColuna();
  const [lateralRecolhida, definirLateralRecolhida] = useState(lerLateralRecolhida);
  const recolhida = coluna && lateralRecolhida;

  // Fora do `setState` porque o valor resultante é conhecido aqui e o
  // atualizador tem de ser puro — em `StrictMode` ele roda duas vezes.
  const alternarLateral = () => {
    const proxima = !lateralRecolhida;
    definirLateralRecolhida(proxima);
    gravarLateralRecolhida(proxima);
  };

  // Navegou, fecha. Gaveta que fica aberta por cima do conteúdo recém-aberto
  // é o defeito clássico deste padrão.
  useEffect(() => definirGavetaAberta(false), [local.pathname]);

  /*
   * Com a gaveta aberta, a página atrás não rola.
   *
   * No celular, arrastar dentro de uma sobreposição arrasta o que está atrás
   * dela; ao fechar, a pessoa está em outro ponto da tela sem ter pedido nada.
   * E `Esc` fecha porque teclado também alcança esta tela — em tablet com
   * teclado, e no navegador de quem só usa teclado.
   */
  useEffect(() => {
    if (!gavetaAberta) return undefined;

    document.body.classList.add("estrutura--travada");
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") definirGavetaAberta(false);
    };
    document.addEventListener("keydown", aoTeclar);

    return () => {
      document.body.classList.remove("estrutura--travada");
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [gavetaAberta]);

  const nome = estado.tipo === "autenticado" ? estado.perfil.nome : "";

  return (
    <div className="estrutura">
      <FaixaAmbiente ambiente={ambiente} />

      <header className="estrutura__cabecalho">
        <button
          type="button"
          className="estrutura__gatilho"
          aria-expanded={gavetaAberta}
          aria-controls="navegacao-principal"
          aria-label={gavetaAberta ? "Fechar navegação" : "Abrir navegação"}
          onClick={() => definirGavetaAberta((aberta) => !aberta)}
        >
          <span aria-hidden="true">{gavetaAberta ? "✕" : "☰"}</span>
        </button>

        {/* A marca é o COMPONENTE, não uma palavra escrita à mão. A variante
            `sobreMarca` existe porque esta barra JÁ É a placa verde-900: a
            institucional desenharia uma segunda placa em cima da primeira, e a
            monocromática é do papel. Trocar o logotipo oficial um dia não toca
            esta linha — o desenho inteiro mora num objeto de `Marca.tsx`. */}
        <Marca variante="sobreMarca" tamanho="pequena" />

        <div className="estrutura__usuario">
          <span className="estrutura__nome">{nome}</span>
          {/* `sobreMarca`, e não `texto` com correção de contexto em `app.css`.
              O botão de texto se pinta com a tinta institucional, que sobre
              esta faixa dá 1.60:1 parado — e o :hover dele pintou **1.09:1**
              num Chrome de verdade, porque a cor vinha de uma regra e o
              assento de outra. A variante viaja com a chamada e leva o par
              inteiro: 15.23:1 parada, 9.82:1 no :hover, 14.03:1 no :active.
              É por isso que o bloco `--fantasma-*` saiu de `app.css`. */}
          <Botao
            aparencia="sobreMarca"
            carregando={saindo}
            onClick={() => {
              definirSaindo(true);
              void sair().finally(() => definirSaindo(false));
            }}
          >
            Sair
          </Botao>
        </div>
      </header>

      <div className="estrutura__corpo">
        <div
          id="navegacao-principal"
          className={
            `estrutura__lateral${gavetaAberta ? " estrutura__lateral--aberta" : ""}`
            + `${recolhida ? " estrutura__lateral--faixa" : ""}`
          }
        >
          <Navegacao
            grupos={SETORES}
            rotaAtual={local.pathname}
            aoNavegar={(rota) => navegar(rota)}
            recolhida={recolhida}
            aoAlternarRecolhimento={coluna ? alternarLateral : undefined}
          >
            {versao !== null && <span>versão {versao}</span>}
          </Navegacao>
        </div>

        {/* Fecha a gaveta ao tocar fora. Sem foco e sem papel de botão: é
            superfície de dispensa, e o botão do cabeçalho já faz o mesmo
            para quem navega por teclado. */}
        {gavetaAberta && (
          <div
            className="estrutura__sombra"
            onClick={() => definirGavetaAberta(false)}
            aria-hidden="true"
          />
        )}

        {/* Duas peças, e a divisão é o conserto de uma barra de rolagem que
            flutuava no meio do claro.
            `.estrutura__conteudo` é quem ROLA, e ele ocupa toda a largura que
            sobra da lateral: assim a barra dele nasce na borda da janela e se
            lê como a barra da página. `.estrutura__coluna` é quem MEDE — os
            64rem de linha legível e o recuo —, e não rola nada.
            Enquanto os dois eram o mesmo elemento, a barra ficava na borda da
            COLUNA: medidos 42,5px de papel claro entre ela e a janela em
            1365px, ou seja uma segunda barra solta no meio da tela. Ver o
            bloco "DESKTOP: a lateral rola sozinha" em `app.css`. */}
        <main className="estrutura__conteudo">
          <div className="estrutura__coluna">{children}</div>
        </main>
      </div>
    </div>
  );
}
